import { useState, useEffect, useCallback } from 'react';
import { getDailyWord, isValidWord } from '../data/words';
import { decodeWord } from '../utils/challengeUtils';

const MAX_ROUNDS = 5;
const MAX_GUESSES = 6;
const WORD_LENGTH = 5;
const CHAIN_THRESHOLD = 4; // Must solve in 4 or fewer guesses to continue

const STORAGE_KEY = 'five-loop-wordle-v2';
const CHALLENGE_STORAGE_KEY = 'five-loop-challenge';

// Get challenge word from URL if present
function getChallengeFromUrl() {
  const params = new URLSearchParams(window.location.search);
  const encoded = params.get('challenge');
  if (encoded) {
    const word = decodeWord(encoded);
    if (word && word.length === 5 && isValidWord(word)) {
      return word;
    }
  }
  return null;
}

// Get today's date string in user's local timezone
function getTodayString() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

// Get milliseconds until midnight in user's local timezone
function getMsUntilMidnight() {
  const now = new Date();
  const midnight = new Date(now);
  midnight.setHours(24, 0, 0, 0);
  return midnight.getTime() - now.getTime();
}

const CHALLENGE_HISTORY_KEY = 'five-loop-challenge-history';

// Create challenge game state
function createChallengeState(challengeWord) {
  return {
    mode: 'playing',
    currentRound: 1,
    roundsCompleted: 0,
    guessesUsedThisRound: 0,
    currentWord: challengeWord,
    usedWords: [challengeWord],
    board: [],
    currentGuess: '',
    letterStates: {},
    history: [],
    lastPlayedDate: getTodayString(),
    streak: 0,
    totalDailyWins: 0,
    message: '',
    shake: false,
    revealRow: -1,
    showCelebration: false,
    isChallenge: true,
    challengeWord: challengeWord,
    challengeStartTime: Date.now(), // Track when challenge started
  };
}

// Create fresh game state with daily words
function createInitialState() {
  const firstWord = getDailyWord(1);
  return {
    mode: 'playing',
    currentRound: 1,
    roundsCompleted: 0,
    guessesUsedThisRound: 0,
    currentWord: firstWord,
    usedWords: [firstWord],
    board: [],
    currentGuess: '',
    letterStates: {},
    history: [],
    lastPlayedDate: getTodayString(),
    streak: 1,
    totalDailyWins: 0,
    message: '',
    shake: false,
    revealRow: -1,
    showCelebration: false,
    isChallenge: false,
  };
}

// Load state from localStorage
function loadState() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;
    return JSON.parse(stored);
  } catch {
    return null;
  }
}

// Save state to localStorage
function saveState(state) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Ignore storage errors
  }
}

// Load challenge history
function loadChallengeHistory() {
  try {
    const stored = localStorage.getItem(CHALLENGE_HISTORY_KEY);
    if (!stored) return [];
    return JSON.parse(stored);
  } catch {
    return [];
  }
}

// Save challenge result to history
function saveChallengeResult(result) {
  try {
    const history = loadChallengeHistory();
    history.unshift(result); // Add to beginning
    // Keep only last 50 entries
    const trimmed = history.slice(0, 50);
    localStorage.setItem(CHALLENGE_HISTORY_KEY, JSON.stringify(trimmed));
  } catch {
    // Ignore storage errors
  }
}

// Evaluate a guess against the target word
function evaluateGuess(guess, target) {
  const result = Array(WORD_LENGTH).fill('absent');
  const targetArr = target.split('');
  const guessArr = guess.split('');
  const used = Array(WORD_LENGTH).fill(false);

  // First pass: mark correct letters
  for (let i = 0; i < WORD_LENGTH; i++) {
    if (guessArr[i] === targetArr[i]) {
      result[i] = 'correct';
      used[i] = true;
    }
  }

  // Second pass: mark present letters
  for (let i = 0; i < WORD_LENGTH; i++) {
    if (result[i] === 'correct') continue;
    for (let j = 0; j < WORD_LENGTH; j++) {
      if (!used[j] && guessArr[i] === targetArr[j]) {
        result[i] = 'present';
        used[j] = true;
        break;
      }
    }
  }

  return result;
}

// Update letter states for keyboard
function updateLetterStates(currentStates, guess, score) {
  const newStates = { ...currentStates };
  const guessArr = guess.split('');
  
  for (let i = 0; i < guessArr.length; i++) {
    const letter = guessArr[i];
    const newState = score[i];
    const existingState = newStates[letter];
    
    if (newState === 'correct') {
      newStates[letter] = 'correct';
    } else if (newState === 'present' && existingState !== 'correct') {
      newStates[letter] = 'present';
    } else if (!existingState) {
      newStates[letter] = 'absent';
    }
  }
  
  return newStates;
}

export function useWordle() {
  const [state, setState] = useState(() => {
    // Check for challenge mode first
    const challengeWord = getChallengeFromUrl();
    if (challengeWord) {
      return createChallengeState(challengeWord);
    }
    
    const loaded = loadState();
    if (!loaded) return createInitialState();
    
    // Check if it's a new day
    const today = getTodayString();
    if (loaded.lastPlayedDate !== today) {
      const newState = createInitialState();
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      const yesterdayStr = `${yesterday.getFullYear()}-${String(yesterday.getMonth() + 1).padStart(2, '0')}-${String(yesterday.getDate()).padStart(2, '0')}`;
      
      newState.streak = loaded.lastPlayedDate === yesterdayStr ? loaded.streak + 1 : 1;
      newState.totalDailyWins = loaded.totalDailyWins;
      return newState;
    }
    
    return loaded;
  });

  const [countdown, setCountdown] = useState('');

  // Save state whenever it changes (but not for challenge mode)
  useEffect(() => {
    if (!state.isChallenge) {
      saveState(state);
    }
  }, [state]);

  // Countdown timer to midnight
  useEffect(() => {
    const updateCountdown = () => {
      const ms = getMsUntilMidnight();
      const hours = Math.floor(ms / (1000 * 60 * 60));
      const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((ms % (1000 * 60)) / 1000);
      setCountdown(`${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`);
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, []);

  // Check for midnight reset
  useEffect(() => {
    const checkReset = () => {
      const today = getTodayString();
      if (state.lastPlayedDate !== today) {
        const newState = createInitialState();
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        const yesterdayStr = `${yesterday.getFullYear()}-${String(yesterday.getMonth() + 1).padStart(2, '0')}-${String(yesterday.getDate()).padStart(2, '0')}`;
        
        newState.streak = state.lastPlayedDate === yesterdayStr ? state.streak + 1 : 1;
        newState.totalDailyWins = state.totalDailyWins;
        setState(newState);
      }
    };

    const interval = setInterval(checkReset, 1000);
    return () => clearInterval(interval);
  }, [state.lastPlayedDate, state.streak, state.totalDailyWins]);

  // Add letter to current guess
  const addLetter = useCallback((letter) => {
    if (state.mode !== 'playing') return;
    if (state.currentGuess.length >= WORD_LENGTH) return;
    
    setState(s => ({
      ...s,
      currentGuess: s.currentGuess + letter.toUpperCase(),
      message: '',
    }));
  }, [state.mode, state.currentGuess.length]);

  // Remove last letter from current guess
  const removeLetter = useCallback(() => {
    if (state.mode !== 'playing') return;
    if (state.currentGuess.length === 0) return;
    
    setState(s => ({
      ...s,
      currentGuess: s.currentGuess.slice(0, -1),
    }));
  }, [state.mode, state.currentGuess.length]);

  // Clear celebration
  const clearCelebration = useCallback(() => {
    setState(s => ({ ...s, showCelebration: false }));
  }, []);

  // Submit current guess
  const submitGuess = useCallback(() => {
    if (state.mode !== 'playing') return;
    
    const guess = state.currentGuess.toUpperCase();
    
    // Validate length
    if (guess.length !== WORD_LENGTH) {
      setState(s => ({ ...s, message: 'Not enough letters', shake: true }));
      setTimeout(() => setState(s => ({ ...s, shake: false })), 500);
      return;
    }
    
    // Validate word
    if (!isValidWord(guess)) {
      setState(s => ({ ...s, message: 'Not in word list', shake: true }));
      setTimeout(() => setState(s => ({ ...s, shake: false })), 500);
      return;
    }
    
    // Evaluate guess
    const score = evaluateGuess(guess, state.currentWord);
    const newBoard = [...state.board, { guess, score }];
    const newGuesses = state.guessesUsedThisRound + 1;
    const isCorrect = guess === state.currentWord;
    const newLetterStates = updateLetterStates(state.letterStates, guess, score);
    
    if (isCorrect) {
      const completed = state.roundsCompleted + 1;
      const qualifiesForNext = newGuesses <= CHAIN_THRESHOLD;
      const wonAllFive = completed >= MAX_ROUNDS;
      const shouldCelebrate = newGuesses <= CHAIN_THRESHOLD; // Celebrate wins in 4 or less
      
      const historyEntry = {
        round: state.currentRound,
        word: state.currentWord,
        guesses: newGuesses,
        success: true,
        chainContinued: qualifiesForNext && !wonAllFive && !state.isChallenge,
      };
      
      // Challenge mode: always end after solving (no rounds)
      if (state.isChallenge) {
        const timeTaken = state.challengeStartTime ? Math.round((Date.now() - state.challengeStartTime) / 1000) : 0;
        
        // Save to challenge history
        saveChallengeResult({
          word: state.currentWord,
          guesses: newGuesses,
          success: true,
          timeTaken,
          date: new Date().toISOString(),
        });
        
        setState(s => ({
          ...s,
          board: newBoard,
          guessesUsedThisRound: newGuesses,
          roundsCompleted: 1,
          currentGuess: '',
          letterStates: newLetterStates,
          history: [...s.history, historyEntry],
          mode: 'chain_ended',
          message: `Solved in ${newGuesses}!`,
          revealRow: newBoard.length - 1,
          showCelebration: shouldCelebrate,
          challengeTimeTaken: timeTaken,
        }));
      } else if (wonAllFive) {
        setState(s => ({
          ...s,
          board: newBoard,
          guessesUsedThisRound: newGuesses,
          roundsCompleted: completed,
          currentGuess: '',
          letterStates: newLetterStates,
          history: [...s.history, historyEntry],
          mode: 'daily_complete',
          message: 'Amazing! You cleared all 5 loops!',
          totalDailyWins: s.totalDailyWins + 1,
          revealRow: newBoard.length - 1,
          showCelebration: true,
        }));
      } else if (qualifiesForNext) {
        // Get the daily word for next round
        const nextRound = state.currentRound + 1;
        const newWord = getDailyWord(nextRound);
        
        setState(s => ({
          ...s,
          board: [],
          guessesUsedThisRound: 0,
          roundsCompleted: completed,
          currentRound: nextRound,
          currentWord: newWord,
          usedWords: [...s.usedWords, newWord],
          currentGuess: '',
          letterStates: {},
          history: [...s.history, historyEntry],
          mode: 'playing',
          message: `Got it in ${newGuesses}! Round ${nextRound} unlocked.`,
          revealRow: -1,
          showCelebration: shouldCelebrate,
        }));
      } else {
        setState(s => ({
          ...s,
          board: newBoard,
          guessesUsedThisRound: newGuesses,
          roundsCompleted: completed,
          currentGuess: '',
          letterStates: newLetterStates,
          history: [...s.history, historyEntry],
          mode: 'chain_ended',
          message: `Solved in ${newGuesses} guesses. Chain ends - needed 4 or fewer.`,
          revealRow: newBoard.length - 1,
          showCelebration: false,
        }));
      }
    } else if (newGuesses >= MAX_GUESSES) {
      // Save challenge loss to history
      if (state.isChallenge) {
        const timeTaken = state.challengeStartTime ? Math.round((Date.now() - state.challengeStartTime) / 1000) : 0;
        saveChallengeResult({
          word: state.currentWord,
          guesses: newGuesses,
          success: false,
          timeTaken,
          date: new Date().toISOString(),
        });
      }
      
      setState(s => ({
        ...s,
        board: newBoard,
        guessesUsedThisRound: newGuesses,
        currentGuess: '',
        letterStates: newLetterStates,
        history: [...s.history, {
          round: s.currentRound,
          word: s.currentWord,
          guesses: newGuesses,
          success: false,
          chainContinued: false,
        }],
        mode: 'chain_ended',
        message: `The word was ${s.currentWord}`,
        revealRow: newBoard.length - 1,
        challengeTimeTaken: state.isChallenge ? Math.round((Date.now() - (state.challengeStartTime || Date.now())) / 1000) : undefined,
      }));
    } else {
      setState(s => ({
        ...s,
        board: newBoard,
        guessesUsedThisRound: newGuesses,
        currentGuess: '',
        letterStates: newLetterStates,
        message: `${MAX_GUESSES - newGuesses} guesses left`,
        revealRow: newBoard.length - 1,
      }));
    }
  }, [state]);

  // Reset game (manual reset)
  const resetGame = useCallback(() => {
    const newState = createInitialState();
    newState.streak = state.streak;
    newState.totalDailyWins = state.totalDailyWins;
    setState(newState);
  }, [state.streak, state.totalDailyWins]);

  // Generate share text - WITHOUT revealing the word
  const getShareText = useCallback(() => {
    const today = getTodayString();
    
    // Different header for challenge vs daily
    const header = state.isChallenge 
      ? 'Five Loop Challenge'
      : `Five Loop ${today}`;
    
    const lines = [header];
    lines.push('');
    
    // For challenge mode, show the board directly
    if (state.isChallenge) {
      if (state.board.length > 0) {
        const emojiGrid = state.board.map(row => 
          row.score.map(s => s === 'correct' ? '🟩' : s === 'present' ? '🟨' : '⬛').join('')
        ).join('\n');
        lines.push(emojiGrid);
      }
      
      if (state.mode === 'chain_ended' || state.mode === 'daily_complete') {
        const won = state.history.length > 0 && state.history[state.history.length - 1]?.success;
        lines.push('');
        lines.push(won ? `Solved in ${state.guessesUsedThisRound}/6!` : 'Better luck next time!');
      }
    } else {
      // Daily mode - show rounds summary
      state.history.forEach((entry) => {
        if (entry.success) {
          lines.push(`Round ${entry.round}: ${entry.guesses}/6 ${entry.chainContinued ? '→' : '✓'}`);
        } else {
          lines.push(`Round ${entry.round}: X/6`);
        }
      });
      
      // Show completed board patterns
      if (state.mode !== 'playing' && state.board.length > 0) {
        lines.push('');
        const emojiGrid = state.board.map(row => 
          row.score.map(s => s === 'correct' ? '🟩' : s === 'present' ? '🟨' : '⬛').join('')
        ).join('\n');
        lines.push(emojiGrid);
      }
      
      if (state.mode === 'daily_complete') {
        lines.push('');
        lines.push('🏆 Completed all 5 loops!');
      }
    }
    
    return lines.join('\n');
  }, [state.history, state.mode, state.board, state.isChallenge, state.guessesUsedThisRound]);

  // Exit challenge mode and return to daily
  const exitChallenge = useCallback(() => {
    // Clear URL params
    window.history.replaceState({}, '', window.location.pathname);
    // Load daily state
    const loaded = loadState();
    if (loaded && !loaded.isChallenge) {
      setState(loaded);
    } else {
      setState(createInitialState());
    }
  }, []);

  // Replay challenge with the same word
  const replayChallenge = useCallback(() => {
    if (state.isChallenge && state.challengeWord) {
      setState(createChallengeState(state.challengeWord));
    }
  }, [state.isChallenge, state.challengeWord]);

  // Get challenge leaderboard stats
  const getChallengeStats = useCallback(() => {
    const history = loadChallengeHistory();
    if (history.length === 0) {
      return {
        totalChallenges: 0,
        wins: 0,
        losses: 0,
        winRate: 0,
        avgGuesses: 0,
        bestGuesses: null,
        avgTime: 0,
        bestTime: null,
        recentGames: [],
      };
    }

    const wins = history.filter(h => h.success);
    const avgGuesses = wins.length > 0 
      ? (wins.reduce((sum, h) => sum + h.guesses, 0) / wins.length).toFixed(1)
      : 0;
    const bestGuesses = wins.length > 0 
      ? Math.min(...wins.map(h => h.guesses))
      : null;
    const avgTime = wins.length > 0
      ? Math.round(wins.reduce((sum, h) => sum + (h.timeTaken || 0), 0) / wins.length)
      : 0;
    const bestTime = wins.length > 0
      ? Math.min(...wins.filter(h => h.timeTaken).map(h => h.timeTaken))
      : null;

    return {
      totalChallenges: history.length,
      wins: wins.length,
      losses: history.length - wins.length,
      winRate: Math.round((wins.length / history.length) * 100),
      avgGuesses: parseFloat(avgGuesses),
      bestGuesses,
      avgTime,
      bestTime: bestTime === Infinity ? null : bestTime,
      recentGames: history.slice(0, 10),
    };
  }, []);

  return {
    // State
    ...state,
    countdown,
    
    // Constants
    maxRounds: MAX_ROUNDS,
    maxGuesses: MAX_GUESSES,
    wordLength: WORD_LENGTH,
    chainThreshold: CHAIN_THRESHOLD,
    
    // Actions
    addLetter,
    removeLetter,
    submitGuess,
    resetGame,
    getShareText,
    clearCelebration,
    exitChallenge,
    replayChallenge,
    getChallengeStats,
  };
}
